import { useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSites } from '../../context/SiteContext';
import { syncRegistry } from '../../lib/registry';

export default function ApiSettingsPage() {
  const { user, refreshToken } = useAuth();
  const { sites } = useSites();
  const [copied, setCopied] = useState(false);
  const [testDomain, setTestDomain] = useState(sites[0]?.domain || '');
  const [result, setResult] = useState('');

  const example = useMemo(
    () =>
      `${window.location.origin}/api/overview?token=${user?.apiToken || 'YOUR_TOKEN'}&domain=${testDomain || 'example.com'}`,
    [user?.apiToken, testDomain]
  );

  const copyToken = async () => {
    if (!user?.apiToken) return;
    await navigator.clipboard.writeText(user.apiToken);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  const runTest = async () => {
    if (!user) return;
    await syncRegistry(user.apiToken, sites);
    try {
      const res = await fetch(
        `/api/overview?token=${encodeURIComponent(user.apiToken)}&domain=${encodeURIComponent(testDomain)}`
      );
      const data = await res.json();
      setResult(JSON.stringify(data, null, 2));
    } catch {
      setResult(JSON.stringify({ code: 500, message: '接口不可用，请确认开发服务已启动' }, null, 2));
    }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
        <span className="w-1.5 h-5 rounded-sm bg-accent-500" />
        API设置
      </h1>
      <p className="mt-2 text-sm text-foreground-600">
        用 Access Token 在自己的网站或插件里读取 PV / IP / UV，不必每次登录后台。
      </p>

      <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6">
        <h2 className="text-sm font-semibold text-foreground-900">Access Token</h2>
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <code className="flex-1 px-4 py-3 rounded-md bg-background-100 text-sm break-all">
            {user?.apiToken || '—'}
          </code>
          <button
            type="button"
            onClick={copyToken}
            className="px-4 py-2 text-sm font-semibold text-background-50 bg-accent-500 rounded-md"
          >
            {copied ? '已复制' : '复制'}
          </button>
          <button
            type="button"
            onClick={() => {
              if (window.confirm('重新生成后，旧 Token 立即失效，确认吗？')) {
                refreshToken().catch((err) => window.alert(err instanceof Error ? err.message : '刷新失败'));
              }
            }}
            className="px-4 py-2 text-sm font-semibold border border-background-300 rounded-md"
          >
            重新生成
          </button>
        </div>
        <p className="mt-3 text-xs text-foreground-500">32 位密钥，请勿泄露。插件里填写这一串即可对接。</p>
      </section>

      <section className="mt-4 rounded-lg border border-background-200 bg-background-50 p-6">
        <h2 className="text-sm font-semibold text-foreground-900">调用说明</h2>
        <ol className="mt-3 space-y-2 text-sm text-foreground-700">
          <li>1. 请求方式：GET</li>
          <li>2. 参数：token（Access Token）、domain（已添加的域名）</li>
          <li>3. 返回今日 / 昨日的浏览量、IP 数、访客数</li>
        </ol>
        <pre className="mt-4 overflow-x-auto rounded-md bg-foreground-950 text-background-50 p-4 text-xs">
          {example}
        </pre>
      </section>

      <section className="mt-4 rounded-lg border border-background-200 bg-background-50 p-6">
        <h2 className="text-sm font-semibold text-foreground-900">测试调用</h2>
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          {sites.length === 0 ? (
            <p className="text-sm text-foreground-500">请先添加站点后再测试接口。</p>
          ) : (
            <>
              <select
                value={testDomain}
                onChange={(e) => setTestDomain(e.target.value)}
                className="flex-1 px-3 py-2 rounded-md border border-background-200 text-sm"
              >
                {sites.map((s) => (
                  <option key={s.id} value={s.domain}>
                    {s.domain}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={runTest}
                className="px-4 py-2 text-sm font-semibold text-background-50 bg-accent-500 rounded-md"
              >
                测试
              </button>
            </>
          )}
        </div>
        {result && (
          <pre className="mt-4 overflow-x-auto rounded-md bg-background-100 p-4 text-xs">
            {result}
          </pre>
        )}
      </section>
    </div>
  );
}
